import React, { useEffect, useState } from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import { Form, FormItemProps, Select, SelectProps, InputNumber, FormInstance, Typography, Flex } from 'antd';
import { t } from 'i18next';

import { RecursivePartial } from 'segments/types/globalTypes';

import { useGetProductsListQuery } from '../store/Api';


interface requiredFormValues {
  extraOptions?: { id: number; amount: number }[];
}
interface Props<FormData> {
  formProps?: FormItemProps;
  selectProps?: Omit<SelectProps, 'onChange'>;
  form: FormInstance<FormData>;
}

export function ProductsSelector<FormData extends requiredFormValues>({ selectProps, form }: Props<FormData>) {
  const { data: products, isLoading } = useGetProductsListQuery();
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  const extraOptions = Form.useWatch('extraOptions', form);

  useEffect(() => {
    if (extraOptions?.length !== selectedProducts.length) {
      let newExtraOptions: { id: number; amount: number }[] = [];
      if ((extraOptions?.length ?? 0) > selectedProducts.length) {
        newExtraOptions = extraOptions?.filter((option) => selectedProducts.some((id) => id === option.id)) ?? [];
      } else {
        newExtraOptions = selectedProducts.map((selectedProduct) => {
          const productOption = extraOptions?.find((option) => option.id === selectedProduct);
          return (
            productOption === undefined
              ? { id: selectedProduct, amount: 1 }
              : { ...productOption }
          );
        });
      }
      form.setFieldsValue({ extraOptions: newExtraOptions } as RecursivePartial<FormData>);
    }
  }, [selectedProducts, extraOptions, form]);

  if (isLoading) {
    return <LoadingOutlined />;
  }

  if (products === undefined) {
    return null;
  }

  return (
    <>
      <Typography.Text >{t('form-items.select.extra-options')}</Typography.Text>
      <Select<number[]>
        showSearch={false}
        {...selectProps}
        mode="multiple"
        style={{ width: '100%', marginTop: 8 }}
        onChange={(ids) => setSelectedProducts(ids)}
      >
        {products
          .filter((product) => product.isOnline)
          .map((product) => (
            <Select.Option key={product.id} value={product.id}>
              {t(`products.items.${product.name}`)}
            </Select.Option>
          ))}
      </Select >
      <Form.List name="extraOptions">
        {(fields) => (
          <Flex style={{ marginTop: '6px' }}>
            {
              fields.map(({ key, name }) => {
                const productName = extraOptions !== undefined
                  && products?.find(({ id }) => extraOptions[name]?.id === id)?.name;
                const formTittle = t(`products.items.${productName}`);

                return (
                  <div key={key} style={{ paddingRight: '4px' }}>
                    <span>{`${formTittle}:`}</span>
                    <Form.Item
                      name={[name, 'amount']}
                      style={{ display: 'inline' }}
                    >
                      <InputNumber min={1} max={9999} />
                    </Form.Item>
                  </div>
                );
              })
            }
          </Flex>
        )}
      </Form.List>
    </>
  );
}
